import React from 'react'
import ReactSelect from 'react-select'
import { useSearchParams } from 'react-router-dom'
import { styled, Stack } from '@mui/material'

//components
import Overview from './components/Overview'
import HourlyTrend from './components/HourlyTrend'
import Rejection from './components/Rejection'
import Idletime from './components/Idletime'
import DateSelector from '../../components/DateSelector'
import IdleTimeDoughnut from '../../components/IdleTimeDoughnut/ChartWithoutControls'

//utils
import * as DATE_UTILS from '../../utils/date.utils'
import { showNotification } from '../../utils/toast.utils'

//service
import { getDataForTimeLineChart } from '../../service/dailyReport.service'
import { getIdleReasonWiseData } from '../../service/idletime.service'
import { useDashboardGlobalStateContext } from '../../context/DashboardGlobalStateContext'

const liveDataTabs = [
  { name: 'Overview', value: 'overview' },
  { name: 'Production', value: 'production' },
  { name: 'Rejection', value: 'rejection' },
  { name: 'Idletime', value: 'idletime' }
]
const customStyles = {
  container: (provided) => ({
    ...provided,
    width: '200px'
  }),
  control: (provided) => ({
    ...provided,
    height: '40px'
  })
}
const TabsContainer = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  padding: '8px 24px 0 0',
  borderBottom: `1px solid #eee`
}))

const Tab = styled('div')(({ theme, isActive }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  fontSize: '16px',
  minWidth: 'fit-content',
  padding: '8px 16px',
  color: isActive ? theme.palette.primary.main : '#ccccd7',
  cursor: 'pointer',
  borderBottom: isActive
    ? `1px solid ${theme.palette.primary.main}`
    : '1px solid transparent',
  '&:hover': {
    color: `${theme.palette.primary.main}`
  }
}))

let defaultTab = liveDataTabs[0].value
let defaultMachine = {
  value: 0,
  label: 'Select Machine'
}

const MachineStatus = () => {
  let [searchParams, setSearchParams] = useSearchParams()
  const tab = searchParams.get('tab')
  const machine = searchParams.get('machine')

  if (tab) {
    defaultTab = tab
  }

  const { machines: machineOptions } = useDashboardGlobalStateContext()

  const [allData, setAllData] = React.useState({})
  const [idletimeData, setIdletimeData] = React.useState({})
  const [loadingState, setLoadingState] = React.useState('idle')
  const [selectedMachine, setSelectedMachine] = React.useState(defaultMachine)
  const [activeSubTab, setActiveSubTab] = React.useState(defaultTab)
  const [availableShifts] = React.useState([
    {
      label: 'S1',
      value: '1'
    },
    {
      label: 'S2',
      value: '2'
    }
  ])
  const [selectedShift, setSelectedShift] = React.useState({
    label: 'S1',
    value: '1'
  })
  const [workingDate, setWorkingDate] = React.useState(DATE_UTILS.workingDate())
  const [calenderDate, setCalenderDate] = React.useState(
    DATE_UTILS.workingDate()
  )

  React.useEffect(() => {
    if (!machine && machineOptions.length > 0) {
      console.log('No machine in URL, setting to default:', machineOptions[0])
      setSelectedMachine(machineOptions[0])
    }

    if (machine && machineOptions.length > 0) {
      console.log('Machine from URL:', machine)
      const foundMachine = machineOptions.find((item) => item.value === machine)
      if (foundMachine) {
        console.log('Found machine:', foundMachine)
        setSelectedMachine(foundMachine)
      } else {
        console.log('Machine not found, setting to default:', machineOptions[0])
        setSelectedMachine(machineOptions[0])
      }
    }
  }, [machine, machineOptions])

  React.useEffect(() => {
    const fetchMachineData = async (workingDateInput, machine, shift) => {
      try {
        setLoadingState('fetching')
        const response = await getDataForTimeLineChart({
          date: workingDateInput,
          machine,
          shift
        })
        if (response.data.success) {
          const liveData = response.data.data
          setAllData(liveData)
        } else {
          showNotification(response.data.message, 'error')
        }
      } catch (err) {
        console.log(err)
        setLoadingState('error')
      } finally {
        setLoadingState('finished')
      }
    }

    if (selectedMachine.value === 0) return

    setSearchParams((prev) => {
      const params = Object.fromEntries(prev.entries())
      params.machine = selectedMachine.value
      return params
    })

    fetchMachineData(workingDate, selectedMachine.value, selectedShift.value)
  }, [workingDate, selectedMachine, selectedShift])

  React.useEffect(() => {
    const fetchData = async (date, machine, shift) => {
      try {
        setLoadingState('fetching')
        const response = await getIdleReasonWiseData({
          date,
          machine,
          shift
        })

        if (response.data.success) {
          setIdletimeData(response.data.data.reasonWiseData)
        } else {
          showNotification(response.data.message, 'error')
        }
      } catch (err) {
        console.log(err)
        showNotification('Failed to fetch data', 'error')
      } finally {
        setLoadingState('finished')
      }
    }

    if (selectedMachine.value !== 0 && selectedShift.value !== 0) {
      fetchData(workingDate, selectedMachine.value, selectedShift.value)
    }
  }, [workingDate, selectedMachine, selectedShift])

  const handleSubTabChange = (tab) => {
    setActiveSubTab(tab)
    setSearchParams((prev) => {
      const params = Object.fromEntries(prev.entries())
      params.tab = tab
      return params
    })
  }

  const dateChangeHandler = (e) => {
    setCalenderDate(e.target.value)
  }

  const submitHandler = (e) => {
    e.preventDefault()
    setWorkingDate(calenderDate)
  }

  const prevNextHandler = (actionType) => {
    setCalenderDate((prevDate) => DATE_UTILS.updateDate(actionType, prevDate))
    setWorkingDate((prevDate) => DATE_UTILS.updateDate(actionType, prevDate))
  }

  const totalIdletime = Object.values(idletimeData)
    .filter((item) => item.time > 0)
    .reduce((acc, curr) => acc + (curr.time || 0), 0)

  let liveDataHourlyGrouped = allData.hourlyGroupedData || []
  let runningTimeData = {
    totalRunningTime: allData.totalTime - totalIdletime,
    totalIdleTime: allData.totalIdleTime || totalIdletime
  }

  const isLoading = ['idle', 'fetching'].includes(loadingState)

  if (isLoading && selectedMachine.value === 0) {
    return <Stack>Loading...</Stack>
  }

  const noData = !isLoading && liveDataHourlyGrouped.length === 0

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #eee',
          ...(isLoading && { pointerEvents: 'none', opacity: 0.9 })
        }}
      >
        <Stack sx={{ flexDirection: 'row', gap: '16px' }}>
          <ReactSelect
            styles={customStyles}
            placeholder="Select Machine"
            options={machineOptions}
            value={selectedMachine}
            isSearchable
            noOptionsMessage={() => 'No machine Found'}
            onChange={(value) => setSelectedMachine(value)}
          />
          <ReactSelect
            styles={customStyles}
            placeholder="Select shift"
            options={availableShifts}
            value={selectedShift}
            onChange={(value) => setSelectedShift(value)}
          />
        </Stack>
        <DateSelector
          date={calenderDate}
          dateChangeHandler={dateChangeHandler}
          submitHandler={submitHandler}
          prevNextHandler={prevNextHandler}
          isLoading={isLoading}
        />
      </Stack>
      <TabsContainer>
        {React.Children.toArray(
          liveDataTabs.map((tab, i) => (
            <Tab
              isActive={activeSubTab === tab.value}
              onClick={() => handleSubTabChange(tab.value)}
            >
              {tab.name}
            </Tab>
          ))
        )}
      </TabsContainer>
      {noData ? (
        <Stack sx={{ padding: '16px' }}>No data available...</Stack>
      ) : (
        <Stack sx={{ height: '100%', overflow: 'auto', padding: '16px' }}>
          {activeSubTab === 'overview' && (
            <Stack sx={{ gap: '16px' }}>
              <Overview
                isLoading={isLoading}
                groupedData={liveDataHourlyGrouped}
                runningTimeData={runningTimeData}
              />
              <IdleTimeDoughnut idleTimeData={idletimeData} />
            </Stack>
          )}
          {activeSubTab === 'production' && (
            <HourlyTrend groupedData={liveDataHourlyGrouped} />
          )}
          {activeSubTab === 'rejection' && (
            <Rejection groupedData={liveDataHourlyGrouped} />
          )}
          {activeSubTab === 'idletime' && (
            <Idletime groupedData={liveDataHourlyGrouped} />
          )}
        </Stack>
      )}
    </Stack>
  )
}

export default MachineStatus
